import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FolderKanban, RefreshCw } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import CenterLayout from '../layout/CenterLayout';
import AppContext from '../Context/AppContext';
import Loader from '../components/Loader';
import { call, projectApi } from '../components/userProject/api';
import UpStats from '../components/userProject/UpStats';
import UpUsers from '../components/userProject/UpUsers';
import UpUserPanel from '../components/userProject/UpUserPanel';
import UpWarnings from '../components/userProject/UpWarnings';
import UpLogs from '../components/userProject/UpLogs';
import UpInvites from '../components/userProject/UpInvites';
import UpWatching from '../components/userProject/UpWatching';
import UpLive from '../components/userProject/UpLive';
import { Toast, useWheelToPage } from '../components/userProject/UpUi';

const TABS = [
    ['users', 'Users'],
    ['watching', 'En lecture'],
    ['warnings', 'Avertissements'],
    ['logs', 'Journal'],
    ['invites', 'Invitations'],
]

/**
 * Users des projets (PrimeStation…) : choix du projet, chiffres, liste paginée des users avec toutes les actions,
 * fiche complète d'un user, avertissements (comportement suspect), journal des connexions, codes d'invitation.
 * Projet, onglet et fiche ouverte dans l'adresse (?p=primestation&tab=users&u=12) : on peut recharger ou partager le lien.
 */
const UserProject = () => {
    const { setCurrentPage } = useContext(AppContext)
    const [params, setParams] = useSearchParams()
    const [projects, setProjects] = useState(null)
    const [error, setError] = useState('')
    const [stats, setStats] = useState(null)
    const [statsError, setStatsError] = useState('')
    const [toast, setToast] = useState(null)
    const [reloadKey, setReloadKey] = useState(0)
    const tabsRef = useWheelToPage()

    useEffect(() => { setCurrentPage('user_project') }, [setCurrentPage])
    useEffect(() => {
        call('get', '/projects').then(setProjects, (e) => setError(e.message))
    }, [])

    const projectId = params.get('p') || projects?.find((p) => p.ready)?.id || projects?.[0]?.id || null
    const project = projects?.find((p) => p.id === projectId) || null
    const tab = TABS.some(([id]) => id === params.get('tab')) ? params.get('tab') : 'users'
    const openUser = params.get('u') ? Number(params.get('u')) : null
    const pApi = useMemo(() => (projectId ? projectApi(projectId) : null), [projectId])

    const setParam = useCallback((changes) => {
        setParams((prev) => {
            const next = new URLSearchParams(prev)
            for (const [k, v] of Object.entries(changes)) {
                if (v == null || v === '') next.delete(k)
                else next.set(k, v)
            }
            return next
        })
    }, [setParams])

    const loadStats = useCallback(async () => {
        if (!pApi || !project?.ready) return
        setStatsError('')
        try { setStats(await pApi.get('/stats')) } catch (e) { setStatsError(e.message) }
    }, [pApi, project])
    useEffect(() => { setStats(null); loadStats() }, [loadStats])

    const notify = useCallback((text, isError = false) => setToast({ text, error: isError, at: Date.now() }), [])
    const changed = useCallback(() => { loadStats(); setReloadKey((n) => n + 1) }, [loadStats])
    const open = useCallback((id) => setParam({ u: id }), [setParam])

    return (
        <MainLayout>
            <CenterLayout>
                <div className='up-page'>
                    <header className='up-head'>
                        <div>
                            <h1>Users projets</h1>
                            <p className='up-muted'>Voir et gérer les comptes de chaque projet</p>
                        </div>
                        <div className='up-projects'>
                            {projects?.map((p) => (
                                <button key={p.id} className={`up-project ${p.id === projectId ? 'on' : ''}`} onClick={() => setParam({ p: p.id, u: null })} title={p.ready ? '' : 'Adresse ou token absent du .env du tracker'}>
                                    <FolderKanban size={16} /> {p.name} {!p.ready && <small>non configuré</small>}
                                </button>
                            ))}
                            {project?.ready && <button className='btn cw up-icon-btn' onClick={changed} title='Tout actualiser'><RefreshCw size={15} /></button>}
                        </div>
                    </header>

                    {error && <p className='up-error'>{error}</p>}
                    {!projects && !error && <div className='up-loading'><Loader /></div>}
                    {project && !project.ready && (
                        <p className='up-error'>{project.name} n'est pas configuré : ajoute {project.id.toUpperCase()}_URL et {project.id.toUpperCase()}_TOKEN dans le .env du tracker.</p>
                    )}

                    {project?.ready && (
                        <>
                            {statsError ? <p className='up-error'>{statsError}</p> : <UpStats stats={stats} />}
                            {/* ce que les gens regardent en ce moment (sport / streaming), relu toutes les 10 s */}
                            <UpLive key={projectId} pApi={pApi} />

                            <nav className='up-tabs' ref={tabsRef}>
                                {TABS.map(([id, label]) => (
                                    <button key={id} className={tab === id ? 'on' : ''} onClick={() => setParam({ tab: id === 'users' ? null : id })}>
                                        {label}
                                        {id === 'warnings' && stats?.warnings.last24h > 0 && <span className='up-tab-count'>{stats.warnings.last24h}</span>}
                                    </button>
                                ))}
                            </nav>

                            {tab === 'users' && <UpUsers key={projectId} pApi={pApi} onOpen={open} notify={notify} reloadKey={reloadKey} onChanged={changed} />}
                            {tab === 'warnings' && <UpWarnings key={projectId} pApi={pApi} project={projectId} onOpen={open} notify={notify} reloadKey={reloadKey} onChanged={changed} />}
                            {tab === 'watching' && <UpWatching key={projectId} pApi={pApi} onOpen={open} reloadKey={reloadKey} />}
                            {tab === 'logs' && <UpLogs key={projectId} pApi={pApi} onOpen={open} reloadKey={reloadKey} />}
                            {tab === 'invites' && <UpInvites key={projectId} pApi={pApi} onOpen={open} notify={notify} reloadKey={reloadKey} onChanged={changed} />}

                            {openUser && (
                                <UpUserPanel key={`${projectId}-${openUser}`} pApi={pApi} userId={openUser} onClose={() => setParam({ u: null })} onOpen={open} notify={notify} onChanged={changed} />
                            )}
                        </>
                    )}
                    <Toast toast={toast} onDone={() => setToast(null)} />
                </div>
            </CenterLayout>
        </MainLayout>
    );
};

export default UserProject;
